import './AddAd.scss';
import 'react-toastify/dist/ReactToastify.css';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 } from 'uuid';
import * as Yup from 'yup';
import styled from 'styled-components';
import { Icon } from '@iconify/react';

import checkMark from '../../assets/images/icons/check-mark.svg';
import chevronLeft from '../../assets/images/icons/chevron-left.svg';
import appPreview from '../../assets/images/app-preview.png';
import Image from '../../components/Image';
import InputForm from '../../components/InputForm/InputForm';
import SelectForm from '../../components/SelectForm/SelectForm';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import AvailabilityCalendar from '../../components/AvailabilityCalendar';
import Button from '../../components/Buttons/Button';
import { colors } from '../../styles/tokens';

const AddAd = () => {
  const navigate = useNavigate();

  const [businessName, setBusinessName] = useState('');
  const [location, setLocation] = useState('');
  const [website, setWebsite] = useState('');
  // Dates are yyyy-MM-dd strings straight off the availability calendar;
  // dateEnd is exclusive (server live window is date_start <= now < date_end).
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [bookedDates, setBookedDates] = useState([]);
  const [about, setAbout] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    businessName: Yup.string().required('Business name is required'),
    about: Yup.string().required('About is required'),
    dateStart: Yup.string().required('Pick the ad dates on the calendar'),
    dateEnd: Yup.string().required('Tap the last day on the calendar to finish selecting'),
  });

  // Same availability source as the ads portal calendar: paid bookings plus
  // active checkout holds. Refetch on window focus so the calendar doesn't go
  // stale while the form sits open.
  const fetchBookedDates = () =>
    api
      .get('/ads/booked-dates')
      .then((res) => setBookedDates(res.data?.booked_dates || []))
      .catch(() => {});

  useEffect(() => {
    fetchBookedDates();
    window.addEventListener('focus', fetchBookedDates);
    return () => window.removeEventListener('focus', fetchBookedDates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddingAd = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await validationSchema.validate(
          {
            businessName,
            selectedVenues: selectedVenues?.value,
            selectedEvents: selectedEvents?.value,
            website,
            dateStart,
            dateEnd,
            about,
          },
          { abortEarly: false }
      );

      const newAd = {
        type: 'default',
        business_name: businessName,
        website,
        date_start: dateStart,
        date_end: dateEnd,
        photo: promoterImg.find((img) => img.file)?.file ?? null,
        about,
      };

      // Venue and event are optional. A brand ad (e.g. a website like
      // Blacklane) isn't tied to either, so only send them when chosen.
      if (selectedVenues?.value) newAd.location = selectedVenues.value;
      if (selectedEvents?.value) newAd.event = selectedEvents.value;

      await api.post('/ads', newAd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setBusinessName('');
      setLocation('');
      setWebsite('');
      setDateStart('');
      setDateEnd('');
      setAbout('');
      setSelectedVenues(null);
      setSelectedEvents(null);
      setPromoterImg([{ id: v4() }]);

      toast.success('Ad added successfully.');
      navigate('/ad-management');
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
        toast.error('Please fix the form errors and try again.');
      } else if (error?.response?.status === 409) {
        // Server-side double-booking guard fired — someone paid for those
        // days since our last fetch. Refresh the calendar to show them.
        toast.error(error.response.data?.message || 'Those dates are already booked.');
        fetchBookedDates();
      } else {
        console.error('Error adding ad', error);
        toast.error('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const [promoterImg, setPromoterImg] = useState([{ id: v4() }]);
  const [previewMode, setPreviewMode] = useState('popup');

  // Single-image mode: each upload replaces the slot's file. Keep the same
  // id so React preserves the Image component instance — its internal state
  // is what renders the photo inside the picker. New id on every upload
  // would unmount + remount, losing the in-component preview.
  const handleImageAdd = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;
    setPromoterImg(([first]) => [{ id: first?.id || v4(), file }]);
  }, []);

  const handleImageRemove = useCallback(() => {
    // Remove DOES rotate the id so the Image component unmounts cleanly
    // (its internal `file` state would otherwise stick around).
    setPromoterImg([{ id: v4() }]);
  }, []);

  // Live preview blob URL — derives from the (now single) selected image.
  const photoPreview = useMemo(() => {
    const fileObj = promoterImg.find((img) => img.file)?.file;
    return fileObj ? URL.createObjectURL(fileObj) : null;
  }, [promoterImg]);

  const [loadingVenuesAndEvents, setLoadingVenuesAndEvents] = useState(true);
  const [listVenues, setListVenues] = useState([]);
  const [listEvents, setListEvents] = useState([]);
  const [selectedVenues, setSelectedVenues] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venuesResponse, eventsResponse] = await Promise.all([
          api.get('/venues', { params: { records: 99999999, page: 1 } }),
          api.get('/events', { params: { records: 99999999, page: 1 } }),
        ]);

        setListVenues(venuesResponse.data.data);
        setListEvents(eventsResponse.data.data);
      } catch (error) {
        console.error('Error fetching venues/events:', error);
      } finally {
        setLoadingVenuesAndEvents(false);
      }
    };

    fetchData();
  }, []);

  const optionsVenus = listVenues.map((venue) => ({
    value: venue._id,
    label: venue.name,
  }));
  const optionsEvents = listEvents.map((event) => ({
    value: event._id,
    label: event.name,
  }));

  const handleCategoryVenues = (selectedOptions) => {
    setSelectedVenues(selectedOptions);
  };
  const handleCategoryEvents = (selectedOptions) => {
    setSelectedEvents(selectedOptions);
  };

  return (
      <main>
        <TopNav />
        <section className="main-container">
          <SideBar />
          <article className="flex w-full justify-center">
            <section className="add-ad">
              <div className="add-ad__top flex-wrap gap-1">
                <div className="add-ad__top-left flex items-center gap-2">
                  <img
                      src={chevronLeft}
                      className="add-ad__top-left-icon"
                      onClick={() => navigate(-1)}
                      alt="Back"
                  />
                  <h1 className="add-ad__top-left-heading">Create New Ad</h1>
                </div>
              </div>

              <form onSubmit={handleAddingAd} className="add-ad__form">
                <section className="space-y-6">
                  <InputForm
                      placeholder="Enter Business Name"
                      label="Business Name"
                      type="text"
                      id="businessName"
                      name="businessName"
                      value={businessName}
                      onChange={(event) => setBusinessName(event.target.value)}
                      error={errors.businessName}
                  />

                  <InputForm
                      placeholder="https://www.example.com"
                      label="Website"
                      type="text"
                      id="website"
                      name="website"
                      value={website}
                      onChange={(event) => setWebsite(event.target.value)}
                      error={errors.website}
                  />

                  <div className="grid gap-4">
                    <SelectForm
                        isLoading={loadingVenuesAndEvents}
                        onChange={handleCategoryVenues}
                        options={optionsVenus}
                        value={selectedVenues}
                        error={errors.selectedVenues}
                        label="Venue (optional)"
                    />
                    <SelectForm
                        isLoading={loadingVenuesAndEvents}
                        onChange={handleCategoryEvents}
                        options={optionsEvents}
                        value={selectedEvents}
                        error={errors.selectedEvents}
                        label="Event (optional)"
                    />
                  </div>

                  <div className="form-group">
                    <label className="add-label mb-2">
                      Dates
                      {(errors.dateStart || errors.dateEnd) && (
                          <span className="error-message"> {errors.dateStart || errors.dateEnd}</span>
                      )}
                    </label>
                    <AvailabilityCalendar
                        bookedDates={bookedDates}
                        start={dateStart}
                        endExclusive={dateEnd}
                        onChange={(s, e) => { setDateStart(s); setDateEnd(e); }}
                    />
                  </div>

                  <div className="image-upload">
                    {promoterImg.map((img) => (
                        <Image
                            key={img.id}
                            onAdd={handleImageAdd}
                            onRemove={() => handleImageRemove(img.id)}
                        />
                    ))}
                  </div>

                  <div className="form-group">
                    <label htmlFor="venueDescription" className="add-label mb-2">
                      About
                      {errors.about && (
                          <span className="error-message"> {errors.about}</span>
                      )}
                    </label>
                    <textarea
                        rows={5}
                        id="venueDescription"
                        name="about"
                        value={about}
                        onChange={(event) => setAbout(event.target.value)}
                        placeholder="Provide a short description for this ad"
                        className="venue-info__form-group-about-input smooth-input about-textarea"
                        maxLength={108}
                    />
                    <span style={{ display: 'block', textAlign: 'right', fontSize: 12, color: '#888', marginTop: 4 }}>{(about || '').length}/108</span>
                  </div>

                  <div>
                    <Button
                        primaryBtn
                        type="submit"
                        label="Add Information"
                        disabled={loading}
                        iconifyIconName="material-symbols:check-rounded"
                    />
                  </div>
                </section>
              </form>

              {/* Live preview — phone-frame mock of how the ad will look in
                  the Zona app. Lives OUTSIDE the form so the form's nested
                  `> section > div { display: flex }` SCSS doesn't break the
                  preview's vertical stacking (or the form's image picker). */}
              <PreviewSection>
                <PreviewHead>
                  <PreviewLabel>LIVE PREVIEW</PreviewLabel>
                  <PreviewTabs>
                    <PreviewTab $active={previewMode === 'popup'} type="button" onClick={() => setPreviewMode('popup')}>
                      Popup
                    </PreviewTab>
                    <PreviewTab $active={previewMode === 'banner'} type="button" onClick={() => setPreviewMode('banner')}>
                      Map banner
                    </PreviewTab>
                  </PreviewTabs>
                </PreviewHead>

                <PhoneStage>
                  <PhoneFrame>
                    <PhoneNotch />
                    <PhoneScreen>
                      <PhoneScreenshot src={appPreview} alt="ZONA heatmap preview" />

                      {previewMode === 'popup' && (
                        <>
                          <PopupBackdrop />
                          <PopupCenter>
                            <PopupCard>
                              <PopupClose>
                                <Icon icon="solar:close-circle-outline" />
                              </PopupClose>
                              {photoPreview ? (
                                <PopupImage style={{ backgroundImage: `url(${photoPreview})` }} />
                              ) : (
                                <PopupImage $placeholder>
                                  <PopupPlaceholderText>Your image</PopupPlaceholderText>
                                </PopupImage>
                              )}
                              <PopupBody>
                                <PopupName>{businessName || 'Your business'}</PopupName>
                                <PopupDesc>{about || 'Your description will appear right here when users open the app.'}</PopupDesc>
                                <PopupCta>
                                  {website ? 'Visit Website' : 'Learn More'}
                                </PopupCta>
                              </PopupBody>
                            </PopupCard>
                          </PopupCenter>
                        </>
                      )}

                      {previewMode === 'banner' && (
                        <BannerSlot>
                          <BannerCard>
                            {photoPreview ? (
                              <BannerImage style={{ backgroundImage: `url(${photoPreview})` }} />
                            ) : (
                              <BannerImage $placeholder />
                            )}
                            <BannerBody>
                              <BannerNameRow>
                                <BannerName>{businessName || 'Your business'}</BannerName>
                                <BannerAdChip>AD</BannerAdChip>
                              </BannerNameRow>
                              <BannerDesc>{about || 'A bit of your description shows here.'}</BannerDesc>
                              <BannerLocRow>
                                <Icon icon="solar:global-linear" width={12} height={12} />
                                <BannerMeta>
                                  {website ? website.replace(/^https?:\/\//, '') : 'Your website'}
                                </BannerMeta>
                              </BannerLocRow>
                            </BannerBody>
                          </BannerCard>
                        </BannerSlot>
                      )}
                    </PhoneScreen>
                  </PhoneFrame>
                </PhoneStage>

                <PreviewCaption>
                  {previewMode === 'popup'
                    ? 'Displayed when users open the app. High visibility from the first screen.'
                    : 'Shown to every user browsing the heatmap while your ad is live.'}
                </PreviewCaption>
              </PreviewSection>
            </section>
          </article>
        </section>
      </main>
  );
};

// Live preview styled components (ported from Zona-ads-portal/CreateAd)

const PreviewSection = styled.div`
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid ${colors.border};
`;

const PreviewHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const PreviewLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: ${colors.brandSoft};
`;

const PreviewTabs = styled.div`
  display: inline-flex;
  background: ${colors.surface1};
  border: 1px solid ${colors.border};
  border-radius: 100px;
  padding: 3px;
`;

const PreviewTab = styled.button`
  background: ${({ $active }) => ($active ? colors.gradient : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : colors.textMuted || '#A1A1AA')};
  border: none;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
  padding: 7px 14px;
  cursor: pointer;
  transition: color 160ms;
`;

const PhoneStage = styled.div`
  display: flex;
  justify-content: center;
  padding: 28px 0;
  border-radius: 22px;
  background:
    radial-gradient(ellipse 400px 300px at 50% 50%, rgba(114,41,245,0.15) 0%, transparent 60%),
    rgba(0, 0, 0, 0.3);
  border: 1px solid ${colors.border};
`;

const PhoneFrame = styled.div`
  position: relative;
  width: 300px;
  height: 610px;
  border-radius: 48px;
  border: 8px solid #2a2a2a;
  background: #000;
  overflow: hidden;
  box-shadow:
    0 30px 60px -20px rgba(0,0,0,0.7),
    0 0 80px rgba(114,41,245,0.18);

  @media (max-width: 600px) { width: 280px; height: 570px; }
`;

const PhoneNotch = styled.div`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 95px;
  height: 22px;
  background: #000;
  border-radius: 100px;
  z-index: 5;
`;

const PhoneScreen = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 40px;
  overflow: hidden;
`;

const PhoneScreenshot = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PopupBackdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(2px);
`;

const PopupCenter = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 0 12px;
`;

const PopupCard = styled.div`
  position: relative;
  width: 240px;
  border-radius: 16px;
  padding: 6px 6px 16px;
  background: linear-gradient(115deg, #0A3FD6 0%, #7229F5 100%);
  opacity: 0.92;
  box-shadow: 0 20px 40px -10px rgba(0,0,0,0.6);
`;

const PopupClose = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #fff;
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 14px;
  z-index: 2;
`;

const PopupImage = styled.div`
  width: 100%;
  height: 150px;
  border-radius: 16px;
  margin-bottom: 14px;
  background-size: cover;
  background-position: center;
  ${({ $placeholder }) => $placeholder && `
    background: rgba(255,255,255,0.1);
    display: grid;
    place-items: center;
  `}
`;

const PopupPlaceholderText = styled.span`
  color: rgba(255,255,255,0.55);
  font-size: 13px;
  font-weight: 600;
`;

const PopupBody = styled.div`
  padding: 0 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PopupName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  line-height: 1.15;
  margin-bottom: 2px;
`;

const PopupDesc = styled.p`
  font-size: 12px;
  color: #fff;
  opacity: 0.9;
  text-align: center;
  line-height: 1.35;
  margin-bottom: 12px;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PopupCta = styled.div`
  display: inline-block;
  min-width: 145px;
  padding: 10px 18px;
  border-radius: 25px;
  background: rgba(2, 2, 4, 0.4);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  align-self: center;
`;

const BannerSlot = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 72px;
  padding: 0 12px;
  display: flex;
  justify-content: center;
`;

const BannerCard = styled.div`
  width: 100%;
  height: 82px;
  display: flex;
  gap: 8px;
  padding: 4px;
  border-radius: 12px;
  background: linear-gradient(135deg, #16161f 0%, #0c0c12 100%);
  border: 1px solid rgba(130,85,255,0.45);
  box-shadow: 0 8px 20px -4px rgba(0,0,0,0.5);
`;

const BannerImage = styled.div`
  width: 74px;
  height: 74px;
  border-radius: 6px;
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  ${({ $placeholder }) => $placeholder && `background: rgba(255,255,255,0.1);`}
`;

const BannerBody = styled.div`
  flex: 1;
  min-width: 0;
  padding: 3px 4px 3px 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const BannerNameRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
`;

const BannerAdChip = styled.span`
  flex-shrink: 0;
  background: rgba(255,255,255,0.2);
  color: #fff;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 1px 4px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const BannerName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BannerDesc = styled.div`
  font-size: 9.5px;
  font-weight: 300;
  color: #fff;
  opacity: 0.6;
  line-height: 1.25;
  margin: 1px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BannerLocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: #fff;
`;

const BannerMeta = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PreviewCaption = styled.p`
  margin-top: 14px;
  font-size: 13px;
  color: ${colors.textMuted || '#A1A1AA'};
  line-height: 1.5;
  text-align: center;
`;

export default AddAd;
